import { isIosPlatform } from '@lighthouse/tools'
import { useCallback, useEffect, useLayoutEffect, useRef } from 'react'

type EditorPosition = {
    top: number
    left: number
}

type CellEditorPositionPayload = {
    /** 滚动容器，用于检测 anchor 何时滚出可视区以关闭 popover */
    scrollElement?: HTMLElement | null
    anchorElement: HTMLDivElement | undefined
    open: boolean
    /** 相对 anchor 四边的微调偏移量（px） */
    offset?: number
    onOpenChange: (val: boolean) => void
}

export const useCellEditorPosition = (payload: CellEditorPositionPayload) => {
    const { scrollElement, anchorElement, open, offset = 1, onOpenChange } = payload

    const popoverRef = useRef<HTMLDivElement>(null)
    const editorPositionRef = useRef<EditorPosition | undefined>()
    const rafRef = useRef<number | undefined>()
    const isIos = isIosPlatform()

    const handleClearAnchorPosition = useCallback(() => {
        editorPositionRef.current = undefined
    }, [])

    /**
     * 计算 popover 的 top 值，超出视口底部时尝试翻转到 anchor 上方。
     * 上方空间也不足则维持原位不处理。
     */
    const getAdjustedTop = useCallback(
        (anchorTop: number, popoverHeight: number): number => {
            const normalBottom = anchorTop + offset + popoverHeight
            if (normalBottom > window.innerHeight) {
                // 翻转到上方：popover 底部贴近 anchor 顶部
                const flippedTop = anchorTop - popoverHeight - offset
                if (flippedTop >= 0) {
                    return flippedTop
                }
            }
            return anchorTop + offset
        },
        [offset]
    )

    /**
     * 直接操作 DOM 更新 popover 位置（供滚动时调用，跳过 React re-render）
     * 同时检测 anchor 是否完全离开 scrollElement 可视区，离开则关闭
     */
    const handleUpdateByDom = useCallback(() => {
        const el = popoverRef.current
        if (!el || !anchorElement) {
            return
        }
        const { top, left, right, bottom } = anchorElement.getBoundingClientRect()

        if (scrollElement) {
            const { top: cTop, bottom: cBottom, left: cLeft, right: cRight } = scrollElement.getBoundingClientRect()
            const isOutOfView = bottom < cTop || top > cBottom || right < cLeft || left > cRight
            if (isOutOfView) {
                onOpenChange(false)
                return
            }
        }

        const popoverHeight = el.getBoundingClientRect().height
        el.style.top = `${getAdjustedTop(top, popoverHeight)}px`
        el.style.left = `${left + offset}px`
    }, [anchorElement, scrollElement, offset, onOpenChange, getAdjustedTop])

    /**
     * 初始化 popover 位置（用于首次渲染，通过 React 状态驱动）
     * position: fixed，直接使用 getBoundingClientRect 视口坐标，无需除以 scale
     */
    const handleChangeAnchorPosition = useCallback(
        (safeBottom = 0) => {
            if (!anchorElement) {
                return
            }
            const { top, left } = anchorElement.getBoundingClientRect()
            editorPositionRef.current = {
                top: top + offset + safeBottom,
                left: left + offset
            }
        },
        [anchorElement, offset]
    )

    /**
     * 首次渲染后同步检测是否需要翻转（useLayoutEffect 在浏览器绘制前执行，无闪烁）
     * handleChangeAnchorPosition 计算时 popover 尚未挂载，高度未知，故在此处修正
     */
    useLayoutEffect(() => {
        const el = popoverRef.current
        if (!el || !anchorElement || !open) {
            return
        }
        const { top } = anchorElement.getBoundingClientRect()
        const { height: popoverHeight } = el.getBoundingClientRect()
        const adjustedTop = getAdjustedTop(top, popoverHeight)

        el.style.top = `${adjustedTop}px`
        if (editorPositionRef.current) {
            editorPositionRef.current.top = adjustedTop
        }
    }, [open, anchorElement, getAdjustedTop])

    const handleResize = useCallback(() => {
        if (!window.visualViewport) {
            return
        }
        const keyboardHeight = window.visualViewport.height - document.documentElement.clientHeight
        handleChangeAnchorPosition(keyboardHeight)
    }, [handleChangeAnchorPosition])

    // iOS 键盘弹出时修正位置
    useEffect(() => {
        const { visualViewport } = window
        if (visualViewport && isIos) {
            visualViewport.addEventListener('resize', handleResize)
            return () => {
                visualViewport.removeEventListener('resize', handleResize)
            }
        }
    }, [handleResize, isIos])

    // 滚动时直接操作 DOM，保持 popover 贴合 anchor，避免 re-render 开销
    useEffect(() => {
        if (!anchorElement || !open) {
            return
        }
        const handleScroll = () => {
            if (rafRef.current !== undefined) {
                cancelAnimationFrame(rafRef.current)
            }
            rafRef.current = requestAnimationFrame(handleUpdateByDom)
        }
        window.addEventListener('scroll', handleScroll, true)
        return () => {
            window.removeEventListener('scroll', handleScroll, true)
            if (rafRef.current !== undefined) {
                cancelAnimationFrame(rafRef.current)
            }
        }
    }, [anchorElement, open, handleUpdateByDom])

    // anchor 出现时初始化位置并打开，anchor 消失时关闭
    useEffect(() => {
        if (anchorElement && !open) {
            handleChangeAnchorPosition()
            onOpenChange(true)
        }
        if (!anchorElement) {
            onOpenChange(false)
            handleClearAnchorPosition()
        }
    }, [handleChangeAnchorPosition, open, anchorElement, onOpenChange, handleClearAnchorPosition])

    return { popoverRef, handleClearAnchorPosition, position: editorPositionRef.current }
}
